import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useArticles } from "@/lib/useArticles";
import { useAuth } from "@/contexts/AuthContext";
import { ShareStoryForm } from "@/components/ShareStoryForm";
import { CrossPostButton } from "@/components/CrossPostButton";
import { labelForLanguage } from "@/lib/useContentLanguage";
import { MapPin, Languages, ShieldCheck } from "lucide-react";

export default function Stories() {
  const [items, setItems] = useState([]);
  const { user } = useAuth();
  const { items: dbStories } = useArticles("story");

  const loadStories = useCallback(() => {
    http.get("/stories").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  const total = dbStories.length + items.length;

  return (
    <div data-testid="stories-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">SUCCESS STORIES</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl">Real people. Real change.</h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Every story is tagged with a location and plotted on the{" "}
        <Link to="/impact-map" className="underline underline-offset-4" style={{ color: "var(--wofy-primary)" }}>
          Community Impact Map
        </Link>{" "}
        so kindness becomes visible.
      </p>

      <ShareStoryForm onCreated={loadStories} />

      {total === 0 ? (
        <div className="mt-14 wofy-card text-center py-16">
          <div className="font-display text-2xl">No stories yet.</div>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Be the first to share a Joy Story — it will appear here and on the Impact Map.
          </p>
        </div>
      ) : (
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dbStories.map((s) => (
            <Link
              key={s.slug}
              to={`/stories/${s.slug}`}
              data-testid={`story-${s.slug}`}
              className="wofy-card !p-0 overflow-hidden group"
            >
              {s.featured_image && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.featured_image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="overline">{(s.category || "STORY").toUpperCase()}</div>
                <div className="font-display text-xl mt-2 leading-snug">{s.title}</div>
                {s.subtitle && <p className="text-sm mt-3" style={{ color: "var(--wofy-text-soft)" }}>{s.subtitle}</p>}
                {s.author_name && <div className="mt-5 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>— {s.author_name.toUpperCase()}</div>}
              </div>
            </Link>
          ))}

          {items.map((s) => (
            <article key={s.id} data-testid={`story-${s.id}`} className="wofy-card !p-0 overflow-hidden relative">
              {user?.is_admin && (
                <div
                  className="flex items-center justify-between gap-2 px-4 py-2 text-[11px] font-mono uppercase tracking-wider"
                  style={{ background: "var(--wofy-surface)", borderBottom: "1px solid var(--wofy-border)", color: "var(--wofy-text-soft)" }}
                  data-testid={`admin-bar-${s.id}`}
                >
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin · site: {s.site_source || "main"}</span>
                  <CrossPostButton storyId={s.id} currentSiteSource={s.site_source} />
                </div>
              )}
              {s.image_url && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="overline">{s.category.toUpperCase()}</div>
                  {s.content_language && s.content_language !== "en" && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border"
                      style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
                      title="Use the Translate To bar above to read this in your language"
                      data-testid={`story-lang-chip-${s.id}`}
                      translate="no"
                    >
                      <Languages className="w-2.5 h-2.5" />
                      Posted in {labelForLanguage(s.content_language)}
                    </span>
                  )}
                </div>
                <div className="font-display text-xl mt-2 leading-snug">{s.title}</div>
                <p className="text-sm mt-3" style={{ color: "var(--wofy-text-soft)" }}>{s.body}</p>
                <div className="mt-5 flex items-center justify-between text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                  <span>— {s.author_name.toUpperCase()}</span>
                  {(s.city || s.country) && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {[s.city, s.country].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
